/**
 * Every binding type that can appear anywhere in the catalogue —
 * including ones that are no longer offered.
 *
 * `SADDLE_STITCH` is **DISCONTINUED**: saddle-stitch books are no longer
 * produced. The value is deliberately RETAINED because historical
 * Orders, PrintNodeOrders, Books and Products were made with it and must
 * keep rendering truthfully:
 *   - the cover-image / thumbnail components branch on it to draw a flat
 *     (spine-less) cover;
 *   - the manufacturing-set and print-node-order views colour, icon and
 *     label it;
 *   - order history has to keep showing what was actually bought.
 *
 * So: do NOT delete it, and do NOT offer it in any NEW selection UI. The
 * draft settings toggle group, the manufacturing static data enums and
 * `RateConfig`'s `bindingType` axis intentionally list PERFECT and COIL
 * only. `isDiscontinuedBindingType()` is the single check to use when
 * code needs to ask "can this still be made?" — see
 * `Cart.canFacilityProduce()`.
 *
 * (Historical note: `PERFECT` used to be spelled `'PREFECT'` here. Every
 * other place in the client — and the data itself — uses `'PERFECT'`, so
 * the enum value never matched anything. Nothing imported the enum at
 * the time, so the typo was latent rather than broken; it is corrected
 * here so the enum is safe to actually use.)
 */
export enum BindingType {
  PERFECT = 'PERFECT',
  SADDLE_STITCH = 'SADDLE_STITCH',
  COIL = 'COIL',
}

/**
 * Binding types that can still be manufactured and ordered today. This
 * is the list any NEW selection UI should be built from.
 */
export const AVAILABLE_BINDING_TYPES: string[] = [
  BindingType.PERFECT,
  BindingType.COIL
];

/**
 * Binding types retained solely so historical records keep rendering.
 * Nothing in this list can be manufactured any more.
 */
export const DISCONTINUED_BINDING_TYPES: string[] = [
  BindingType.SADDLE_STITCH
];

/**
 * True when `bindingType` names a product we no longer produce. Tolerant
 * of case and of empty / missing values — an unknown or blank binding is
 * NOT treated as discontinued, because that's an "unknown capability"
 * question for the per-facility rules rather than a discontinued-product
 * question.
 */
export function isDiscontinuedBindingType(bindingType: string): boolean {
  if (!bindingType) { return false; }
  return DISCONTINUED_BINDING_TYPES.indexOf(('' + bindingType).toUpperCase()) >= 0;
}

